import { getBackendUrl } from "@Utils";
import { t, untranslated } from "@i18n";
import axiosWrapper from "@AxiosWrapper";
import * as Sentry from "@Sentry";
import { getShareViewerSession } from "@Features/external-share/shareViewerSession";
import { mintSharedViewUrl } from "@Features/external-share/sharedLink.api";
import { setBrowserPopupBlocked } from "../reducers/browserPopupSlice";
import { store } from "@Store";
import { showToastNotification } from "@Reducers/toastSlice";
import { TOAST_TYPE_ERROR, TOAST_TYPE_SUCCESS } from "./constants";
// Type only — erased at build, so `jszip` stays out of the entry bundle and behind the dynamic
// import in `saveRemoteFiles`.
import type JSZip from "jszip";
import type { CreateTinyUrlResponse } from "@ServerTypes/fileUpload/fileUpload.schema";
import type { PdfExportResponse } from "@ServerTypes/pdfExport/pdfExport.schema";

declare global {
  interface Window {
    ReactNativeWebView?: {
      postMessage: (message: string) => void;
    };
  }
}

/**
 * A download request. Every field is optional because each call site sends only the subset its
 * endpoint reads, and the index signature absorbs the extra keys individual callers pass through
 * (the request object is forwarded whole to `redirectDownload`).
 */
interface DownloadRequest {
  /** fileSystemServer route segment — "View", "Download", … */
  type?: string;
  fileVersionIds?: string[];
  folderIds?: string[];
  issuesIds?: string[];
  formData?: { formId?: string; projectId?: string; paperSize?: string; tableSectionFormat?: string };
  mailId?: string;
  projectId?: string;
  /** `boolean | string` because it is handed straight to URLSearchParams, which stringifies it. */
  downloadAllThreads?: boolean | string;
  transmittalNo?: string;
  fileName?: string;
  name?: string;
  estimateId?: string;
  versionId?: string;
  reviewId?: string;
  mobileFileManager?: boolean;
  [key: string]: unknown;
}

/** Invoked with the fetched blob when the caller wants the bytes instead of a browser download. */
type DownloadCallback = (blob: Blob, fileName: string) => void;

function prepareDownloadData(data: DownloadRequest) {
  let objCount = 1;
  let fileVersionIds, folderIds, issuesIds, formId, projectId, mailId, downloadAllThreads;
  // KNOWN BUG, preserved verbatim: the guard is an OR but both arrays are dereferenced, so a
  // request carrying only one of them throws. The `!`s are erased.
  if (data.fileVersionIds || data.folderIds) {
    objCount = data.fileVersionIds!.length + data.folderIds!.length;
    fileVersionIds = data.fileVersionIds!.join(",");
    folderIds = data.folderIds!.join(",");
  } else if (data.issuesIds) {
    objCount = data.issuesIds.length;
    issuesIds = data.issuesIds.join(",");
  } else if (data.formData) {
    formId = data.formData.formId;
    projectId = data.formData.projectId;
  } else if (data.mailId) {
    mailId = data.mailId;
    projectId = data.projectId;
    downloadAllThreads = data.downloadAllThreads || false;
  }
  return { objCount, fileVersionIds, folderIds, issuesIds, formId, projectId, mailId, downloadAllThreads };
}

function setFileAndFolderParams(baseUrl: URL, data: DownloadRequest, fileVersionIds: string, folderIds: string) {
  baseUrl.searchParams.set("fileVersionIds", fileVersionIds);
  baseUrl.searchParams.set("folderIds", folderIds);
  if (data.transmittalNo) {
    // `fileName` is not part of the guard above; a transmittal download with no name has always
    // sent the literal "undefined". The `!` is erased.
    baseUrl.searchParams.set("fileName", data.fileName!);
    baseUrl.searchParams.set("transmittalNo", data.transmittalNo);
  }
}

function setFormParams(baseUrl: URL, data: DownloadRequest, formId: string, projectId: string) {
  baseUrl.searchParams.set("formId", formId);
  baseUrl.searchParams.set("projectId", projectId);
  // Reached only through the `formId` branch of setUrlParameters, which is derived from
  // `data.formData` — hence the erased assertions here.
  if (data.formData!.paperSize) {
    baseUrl.searchParams.set("paperSize", data.formData!.paperSize!);
  }
  if (data.formData!.tableSectionFormat) {
    baseUrl.searchParams.set("tableSectionFormat", data.formData!.tableSectionFormat!);
  }
}

function setEstimateParams(baseUrl: URL, data: DownloadRequest) {
  baseUrl.searchParams.set("estimateId", data.estimateId!);
  if (data.versionId) {
    baseUrl.searchParams.set("versionId", data.versionId);
  }
}

function setMailParams(baseUrl: URL, data: DownloadRequest, projectId: string) {
  baseUrl.searchParams.set("mailId", data.mailId!);
  baseUrl.searchParams.set("projectId", projectId);
  if (data.downloadAllThreads) {
    baseUrl.searchParams.set("downloadAllThreads", data.downloadAllThreads as string);
  }
}

function setUrlParameters(baseUrl: URL, data: DownloadRequest, preparedData: ReturnType<typeof prepareDownloadData>) {
  const { fileVersionIds, folderIds, issuesIds, formId, projectId } = preparedData;

  if (fileVersionIds || folderIds) {
    // The guard above is `fileVersionIds || folderIds`, so only ONE is guaranteed; the other has
    // always been serialised as the literal "undefined". Erased assertions, same as `fileName`.
    setFileAndFolderParams(baseUrl, data, fileVersionIds!, folderIds!);
  } else if (issuesIds) {
    baseUrl.searchParams.set("issuesIds", issuesIds);
  } else if (formId) {
    setFormParams(baseUrl, data, formId!, projectId!);
  } else if (data.estimateId) {
    setEstimateParams(baseUrl, data);
  } else if (data.reviewId) {
    baseUrl.searchParams.set("reviewId", data.reviewId);
  } else if (data.mailId) {
    setMailParams(baseUrl, data, projectId!);
  }
  baseUrl.searchParams.set("origin", location.hostname);
}

function handleCallbackDownload(
  data: DownloadRequest,
  preparedData: ReturnType<typeof prepareDownloadData>,
  callback: DownloadCallback,
) {
  // CP-283 recipient share view: the viewers that fetch bytes through this callback path (Panorama
  // for images, HtmlViewer) hold no CUBE session, and /fileSystemServer/* would answer 401. The
  // bytes come from an HMAC-ticketed public URL instead — minted per request, containment-checked
  // server-side, inline-tier so Can View links work. Branching HERE, at the one choke point, is what
  // keeps every downloadFile(callback) consumer share-safe without per-component plumbing.
  //
  // This branch keys on the SHARE SESSION, not on "is the user logged in": an employee opening a
  // share link must also go through the ticket path, or they would fetch with their OWN permissions
  // and see the feature "work" in ways a real recipient never could.
  const shareSession = getShareViewerSession();
  if (shareSession) {
    const versionId = preparedData.fileVersionIds?.split(",")[0];
    if (!versionId) return;
    mintSharedViewUrl(shareSession, versionId)
      .then(async (url) => {
        if (!url) throw new Error("share view ticket refused");
        // Bare fetch, never axiosWrapper: the wrapper attaches auth headers and its interceptors own
        // 401 handling — both wrong for a public ticketed URL.
        const response = await fetch(url);
        if (!response.ok) throw new Error(`share view fetch failed: ${response.status}`);
        const blob = await response.blob();
        const finalFileName = data.fileName || data.name;
        callback(blob, finalFileName!);
      })
      .catch((error: unknown) => {
        // Reported, not retried: by far the likeliest cause is the link dying mid-session
        // (revoked/expired), and the page-level resolve loop is what surfaces that to the recipient.
        Sentry.captureException(error);
      });
    return;
  }

  const callbackUrl = new URL(`${getBackendUrl()}/fileSystemServer/${data.type}`);
  const { fileName } = data;

  // Optional on the request; passed through unguarded, same as `fileName` below.
  callbackUrl.searchParams.set("fileVersionIds", preparedData.fileVersionIds!);
  // Both names are optional on the request and have always been passed through unguarded.
  callbackUrl.searchParams.set("fileName", fileName!);

  // fileSystemServer binary blob response - no typed schema
  axiosWrapper.get<Blob>(callbackUrl.toString(), { responseType: "blob" }).then((response) => {
    const finalFileName = data.fileName || data.name;
    callback(response.data, finalFileName!);
  });
}

function handleTinyUrlDownload(data: DownloadRequest, preparedData: ReturnType<typeof prepareDownloadData>) {
  const { fileVersionIds, folderIds, issuesIds } = preparedData;

  axiosWrapper
    .post<CreateTinyUrlResponse>(`${getBackendUrl()}/fileSystemServer/createTinyUrl`, {
      fileVersionIds,
      folderIds,
      issuesIds,
    })
    .then((res) => {
      const tinyUrl = new URL(`${getBackendUrl()}/fileSystemServer/${data.type}`);
      tinyUrl.searchParams.set("tinyUrl", res.data.tinyUrl);
      tinyUrl.searchParams.set("origin", location.hostname);

      const newWindow = redirectDownload(tinyUrl.toString(), data);
      store.dispatch(setBrowserPopupBlocked(!newWindow));
    });
}

function handleRegularDownload(data: DownloadRequest, preparedData: ReturnType<typeof prepareDownloadData>) {
  const baseUrl = new URL(`${getBackendUrl()}/fileSystemServer/${data.type}`);
  setUrlParameters(baseUrl, data, preparedData);

  const newWindow = redirectDownload(baseUrl.toString(), data);
  store.dispatch(setBrowserPopupBlocked(!newWindow));
}

export const downloadFile = (data: DownloadRequest, callback?: DownloadCallback) => {
  const preparedData = prepareDownloadData(data);

  if (callback) {
    handleCallbackDownload(data, preparedData, callback);
    return;
  }

  if (preparedData.objCount > 80) {
    handleTinyUrlDownload(data, preparedData);
  } else {
    handleRegularDownload(data, preparedData);
  }
};

function redirectDownload(url: string, data: DownloadRequest) {
  if (data.mobileFileManager) {
    // The mobile path only ever runs for file/folder downloads, so both arrays are present.
    const nameCondition = data.fileVersionIds!.length == 1 && data.folderIds!.length == 0;
    (globalThis as unknown as Window).ReactNativeWebView?.postMessage(
      JSON.stringify({
        action: "fileDownload",
        data: {
          url,
          fileName: nameCondition
            ? data.fileName
            : `CUBE_Download_${new Date()
                .toLocaleDateString("en-IN", {
                  year: "2-digit",
                  month: "2-digit",
                  day: "2-digit",
                })
                .replaceAll("/", "-")}.zip`,
        },
      }),
    );
    return true;
  }
  const popup = globalThis.open(url, "_blank");
  return !!popup;
}

/** One already-named file to pull down, as `saveRemoteFiles` takes them. */
export type RemoteFileToSave = { name: string; url: string };

/**
 * How many of a bulk download's files are fetched at once. The server caps an upload at 25 MB, so a
 * field with twenty of them is half a gigabyte — fetching the set in one `Promise.all` peaks at that
 * whole figure in transfer buffers. Six matches what a browser will open against one host anyway.
 * `formEntries.tsx` limits its image fetches the same way, at 10.
 */
const MAX_CONCURRENT_DOWNLOADS = 6;

/** Zip output held in the JS heap before being handed to blob storage. See `zipToBlob`. */
const ZIP_FLUSH_BYTES = 8 * 1024 * 1024;

/**
 * Most a bulk download will decode onto the JS heap. JSZip holds every entry's decoded bytes at once
 * (see `zipToBlob`), so this bounds the archive as a whole rather than any one file in it — the server's
 * 25 MB per-upload cap (`MAX_FORM_FILE_UPLOAD_SIZE`) does nothing for a field holding forty of them.
 * Roughly ten uploads' worth: enough for every field this product has, and short of the heap a tab has.
 */
const MAX_BULK_ZIP_BYTES = 256 * 1024 * 1024;

/**
 * Fetches every file, at most `MAX_CONCURRENT_DOWNLOADS` at a time, and returns the blobs in the
 * order they were asked for — a worker pool over a shared cursor rather than a queue of thunks.
 *
 * Rejects with the first failure and aborts whatever is still in flight, so a bulk download that
 * cannot complete stops pulling bytes for an archive that will never be built.
 */
async function fetchInBatches(files: RemoteFileToSave[]): Promise<Blob[]> {
  const blobs = new Array<Blob>(files.length);
  const controller = new AbortController();
  let next = 0;

  const worker = async () => {
    while (next < files.length && !controller.signal.aborted) {
      const index = next++;
      const { name, url } = files[index]!;
      const response = await fetch(url, { signal: controller.signal });
      if (!response.ok) throw new Error(`Failed to download ${name}`);
      blobs[index] = await response.blob();
    }
  };

  try {
    await Promise.all(Array.from({ length: Math.min(MAX_CONCURRENT_DOWNLOADS, files.length) }, worker));
  } catch (error) {
    controller.abort();
    throw error;
  }
  return blobs;
}

/**
 * Generates the archive without ever holding the finished zip in the JS heap.
 *
 * `generateAsync({ type: "blob" })` collects every chunk of the finished zip in an array and only
 * then makes a Blob of it, so the entire archive sits in the heap at once — on top of the source
 * files, which is where a large field turns into a tab crash. Streaming the same generation and
 * handing each `ZIP_FLUSH_BYTES` worth of chunks to a Blob puts that data in the browser's blob
 * storage, which spills to disk, so what the heap holds of the OUTPUT is one flush window regardless
 * of the archive's size.
 *
 * The INPUT is not bounded, and cannot be while JSZip is doing the zipping: `zip.file(name, blob)`
 * starts a `FileReader.readAsArrayBuffer` immediately (`prepareContent` in `jszip/lib/utils.js`) and
 * the decoded `Uint8Array` stays reachable from that entry's `ZipObject._data` until the whole JSZip
 * instance is dropped — so adding the entries lazily moves the peak but does not lower it, and every
 * entry's bytes are on the heap together by the time generation ends. `saveRemoteFiles` refuses a set
 * larger than `MAX_BULK_ZIP_BYTES` instead.
 *
 * The default (non-`streamFiles`) generation is kept: it buffers one entry at a time to get its CRC
 * before writing the header, which the 25 MB upload cap bounds, and it produces an archive with no
 * data descriptors for the sake of the older extractors that mishandle them.
 */
function zipToBlob(zip: JSZip): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const parts: Blob[] = [];
    let pending: Uint8Array[] = [];
    let pendingBytes = 0;

    const flush = () => {
      if (!pendingBytes) return;
      // JSZip types a chunk as `Uint8Array<ArrayBufferLike>`, which `BlobPart` rejects because such a
      // view could in principle be over a SharedArrayBuffer. Generated chunks never are.
      parts.push(new Blob(pending as BlobPart[]));
      pending = [];
      pendingBytes = 0;
    };

    zip
      .generateInternalStream({ type: "uint8array" })
      .on("data", (chunk) => {
        pending.push(chunk);
        pendingBytes += chunk.length;
        if (pendingBytes >= ZIP_FLUSH_BYTES) flush();
      })
      .on("error", reject)
      .on("end", () => {
        flush();
        resolve(new Blob(parts, { type: "application/zip" }));
      })
      .resume();
  });
}

/**
 * Saves a set of remote files to the user's device: one on its own, several as a zip named
 * `archiveName`.
 *
 * This is the browser half of a bulk download. Everything that decides *what* to download — which
 * entries have a storage key, what each is called, what the archive is called — is platform-neutral and
 * lives in `useForms`; only this last step has to know about Blobs, object URLs and `<a download>`, and
 * so is injected into the hook (native's equivalent goes through the filesystem and the share sheet).
 *
 * Zipped in the browser rather than server-side because these are bare storage keys: `downloadFile`
 * above addresses File Manager versions and folders, which is not what a form's file answer holds.
 * `jszip` is imported dynamically so a library only bulk download needs stays out of the entry bundle.
 *
 * Rejects if any file cannot be fetched, or if the set is too large to zip in the browser, for the
 * caller to report.
 */
export async function saveRemoteFiles(files: RemoteFileToSave[], archiveName: string) {
  if (!files.length) return;

  const blobs = await fetchInBatches(files);

  const save = (blob: Blob, fileName: string) => {
    const objectUrl = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = objectUrl;
    anchor.download = fileName;
    anchor.click();
    // The object URL pins the blob for the life of the document, so it has to be released — but only
    // Chromium takes the data over synchronously on `click()`. Firefox and Safari start reading after
    // the current task, and on a large blob they are still reading well into it, so revoking here
    // truncates the download. Held for a minute instead, which outlasts the handover.
    setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
  };

  if (files.length === 1) {
    save(blobs[0]!, files[0]!.name);
    return;
  }
  // Only the zip path is capped, and only once the sizes are known — the caller has names and URLs,
  // and a Blob costs the tab nothing to hold (browser storage, spilled to disk), so a single file of
  // any size still saves. It is `zip.file` that decodes the whole set onto the heap at once.
  const totalBytes = blobs.reduce((total, blob) => total + blob.size, 0);
  if (totalBytes > MAX_BULK_ZIP_BYTES) {
    throw new Error(`Bulk download of ${totalBytes} bytes is more than the ${MAX_BULK_ZIP_BYTES} a browser will zip`);
  }
  const { default: JSZip } = await import("jszip");
  const zip = new JSZip();
  files.forEach((file, index) => zip.file(file.name, blobs[index]!));
  save(await zipToBlob(zip), `${archiveName}.zip`);
}

export const enum EXPORT_TYPE {
  PDF = "PDF",
  EXCEL = "EXCEL",
}
interface PdfExportParams {
  issuesIds?: string[];
  formId?: string | null;
  projectId?: string | null;
  paperSize?: string | null;
  tableSectionFormat?: string | null;
  estimateId?: string | null;
  versionId?: string | null;
  reviewId?: string | null;
  mailId?: string | null;
  downloadAllThreads?: boolean;
  invoiceId?: string | null;
  supportReport?: boolean;
  supportCreatedBy?: string | null;
  supportWhere?: string | null;
  exportType?: EXPORT_TYPE;
}

export async function exportPdf(params: PdfExportParams) {
  try {
    // Build query string
    const queryParams = new URLSearchParams();

    if (params.issuesIds) {
      queryParams.append("issuesIds", params.issuesIds.join(","));
    }
    if (params.formId) {
      queryParams.append("formId", params.formId);
    }
    if (params.projectId) {
      queryParams.append("projectId", params.projectId);
    }
    if (params.paperSize) {
      queryParams.append("paperSize", params.paperSize);
    }
    if (params.tableSectionFormat) {
      queryParams.append("tableSectionFormat", params.tableSectionFormat);
    }
    if (params.estimateId) {
      queryParams.append("estimateId", params.estimateId);
    }
    if (params.versionId) {
      queryParams.append("versionId", params.versionId);
    }
    if (params.reviewId) {
      queryParams.append("reviewId", params.reviewId);
    }
    if (params.mailId) {
      queryParams.append("mailId", params.mailId);
    }
    if (params.downloadAllThreads !== undefined) {
      queryParams.append("downloadAllThreads", String(params.downloadAllThreads));
    }
    if (params.invoiceId) {
      queryParams.append("invoiceId", params.invoiceId);
    }
    if (params.supportReport) {
      queryParams.append("supportReport", "true");
    }
    if (params.supportCreatedBy) {
      queryParams.append("supportCreatedBy", params.supportCreatedBy);
    }
    if (params.supportWhere) {
      queryParams.append("supportWhere", params.supportWhere);
    }

    // Create export job
    const response = await axiosWrapper.get<PdfExportResponse>(
      `${getBackendUrl()}/pdfExport/pdf-export?${queryParams.toString()}`,
    );

    if (!response.data.status) {
      throw new Error(response.data.message || "Failed to create export job");
    }

    // Show success toast
    store.dispatch(
      showToastNotification({
        content: t("PDF export started. You'll be notified when ready."),
        type: TOAST_TYPE_SUCCESS,
      }),
    );

    return {
      status: true,
      jobId: response.data.jobId,
    };
  } catch (error: unknown) {
    console.error("❌ PDF export error:", error);
    store.dispatch(
      showToastNotification({
        content: untranslated((error as Error).message || "Failed to create PDF export"),
        type: TOAST_TYPE_ERROR,
      }),
    );

    return {
      status: false,
      error: (error as Error).message,
    };
  }
}
